import { useCallback, useEffect, useRef, useState } from "react";

/* =====================================================================
   Shared behaviour for the three home-page instruments.

   All three are the same interaction with different data: a set of steps,
   one of which is current, that can be stepped by click or arrow key and
   played through once when the figure first comes into view. Keeping that
   in one hook is what stops the three from drifting apart.
   ===================================================================== */

/** Matches the OS setting, and keeps matching it if the user changes it mid-session. */
export function useReducedMotion(): boolean {
  // SSR-safe: the prerender pass has no matchMedia, and guessing "false" there
  // is correct — the client re-reads on mount before anything animates.
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/** A list that is known to have at least one item, so `items[0]` is never
    undefined under noUncheckedIndexedAccess and the current item needs no
    assertion at the call site. */
export type NonEmpty<T> = readonly [T, ...T[]];

export interface StepperOptions<T> {
  /** The steps themselves. */
  items: NonEmpty<T>;
  /** Milliseconds between steps while playing. */
  intervalMs: number;
  /** Skip autoplay entirely (reduced motion). */
  reduced: boolean;
}

export interface Stepper<T> {
  at: number;
  /** The step `at` points to. Always defined — see NonEmpty. */
  current: T;
  playing: boolean;
  /** Ref to attach to the figure — arms the play-once-on-view observer. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  go: (i: number) => void;
  toggle: () => void;
  /** Arrow / Home / End handling for a tablist. */
  onKeyDown: (e: React.KeyboardEvent) => void;
}

export function useStepper<T>({ items, intervalMs, reduced }: StepperOptions<T>): Stepper<T> {
  const count = items.length;
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    setPlaying(false);
  }, []);

  const go = useCallback(
    (i: number) => {
      stop();
      setAt(Math.min(count - 1, Math.max(0, i)));
    },
    [count, stop],
  );

  const play = useCallback(() => {
    // Replaying from the end restarts rather than sitting on the last step.
    setAt((cur) => (cur >= count - 1 ? 0 : cur));
    setPlaying(true);
  }, [count]);

  // The interval lives in an effect keyed on `playing` rather than being
  // started inside play(), so a component unmounting mid-play cannot leave a
  // timer running against a dead setState.
  useEffect(() => {
    if (!playing) return;
    timer.current = setInterval(() => {
      setAt((cur) => {
        if (cur >= count - 1) {
          setPlaying(false);
          return cur;
        }
        return cur + 1;
      });
    }, intervalMs);
    return () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [playing, count, intervalMs]);

  const toggle = useCallback(() => {
    if (playing) stop();
    else play();
  }, [playing, play, stop]);

  // Play through once, the first time the figure is actually looked at. The
  // argument of each figure IS the sequence, so showing it beats waiting to
  // be asked — but only once, and never under reduced motion.
  const hasPlayed = useRef(false);
  useEffect(() => {
    if (reduced || hasPlayed.current) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasPlayed.current) {
            hasPlayed.current = true;
            io.disconnect();
            window.setTimeout(() => setPlaying(true), 450);
          }
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      let next: number | null = null;
      if (e.key === "ArrowRight") next = Math.min(count - 1, at + 1);
      else if (e.key === "ArrowLeft") next = Math.max(0, at - 1);
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = count - 1;
      if (next === null) return;
      e.preventDefault();
      go(next);
      // Roving tabindex: move focus with the selection so the arrow keys keep
      // working without the user tabbing back into the strip.
      const tabs = e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]');
      tabs[next]?.focus();
    },
    [at, count, go],
  );

  // `?? items[0]` can only fire if `at` were somehow out of range; the tuple
  // type makes the fallback itself provably defined.
  const current = items[at] ?? items[0];

  return { at, current, playing, containerRef, go, toggle, onKeyDown };
}

/** Re-triggers the scan sweep whenever `key` changes. */
export function useSweep(key: number, reduced: boolean) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    if (parent) {
      el.style.setProperty("--sweep-end", `${parent.offsetWidth + 80}px`);
    }
    el.classList.remove("is-on");
    // Force a reflow so removing and re-adding the class restarts the
    // animation instead of being coalesced into no change at all.
    void el.offsetWidth;
    el.classList.add("is-on");
  }, [key, reduced]);

  return ref;
}

/** Counts an integer readout up on change. Non-numeric values pass through. */
export function useCountUp(value: string, reduced: boolean): string {
  const [shown, setShown] = useState(value);

  useEffect(() => {
    const numeric = Number(value.replace(/,/g, ""));
    const suffix = value.replace(/^[\d,]+/, "");
    if (reduced || !Number.isFinite(numeric) || value.trim() === "") {
      setShown(value);
      return;
    }

    let raf = 0;
    const start = performance.now();
    const duration = 430;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / duration);
      const eased = Math.round(numeric * (1 - Math.pow(1 - k, 3)));
      setShown(
        (value.includes(",") ? eased.toLocaleString("en-US") : String(eased)) + suffix,
      );
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, reduced]);

  return shown;
}
